import { useMemo, useState } from "react";

export function usePagination<T>(items: readonly T[], pageSize = 7) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  return {
    page: currentPage,
    totalPages,
    setPage,
    paginatedItems,
    totalItems: items.length,
    pageSize,
  };
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize = 7,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = totalItems ? Math.min(currentPage * pageSize, totalItems) : currentPage * pageSize;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 font-dash text-dash-xs">
      <div className="text-muted font-medium">
        {totalItems ? (
          <span>
            Showing <strong className="text-ink">{startItem}</strong>–<strong className="text-ink">{endItem}</strong> of <strong className="text-ink">{totalItems}</strong> items
          </span>
        ) : (
          <span>
            Page <strong className="text-ink">{currentPage}</strong> of <strong className="text-ink">{totalPages}</strong>
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="rounded-lg border border-line bg-surface px-3 py-1.5 font-bold text-ink transition-colors hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Previous
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
          if (
            totalPages > 7 &&
            p !== 1 &&
            p !== totalPages &&
            Math.abs(p - currentPage) > 1
          ) {
            if (p === 2 && currentPage > 3) return <span key={`dots-start-${p}`} className="px-1 text-muted">…</span>;
            if (p === totalPages - 1 && currentPage < totalPages - 2) return <span key={`dots-end-${p}`} className="px-1 text-muted">…</span>;
            return null;
          }
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 rounded-lg border font-bold transition-colors cursor-pointer ${
                p === currentPage
                  ? "bg-primary text-white border-primary"
                  : "border-line bg-surface text-ink hover:bg-canvas"
              }`}
            >
              {p}
            </button>
          );
        })}
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="rounded-lg border border-line bg-surface px-3 py-1.5 font-bold text-ink transition-colors hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Next
        </button>
      </div>
    </div>
  );
}
